import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitBranch, History, RotateCcw, Eye } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fdate, money, pct } from "@/lib/fmt";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";



const Detail = ({ s }) => (
  <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200" data-testid="costing-rev-detail">
    <table className="tbl w-full text-xs font-num">
      <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Cost/Unit</th><th className="text-right">Harga Jual/Unit</th><th className="text-right">Total</th></tr></thead>
      <tbody>
        {(s.items || []).map((it, i) => <tr key={i}><td className="font-sans">{it.name}</td><td className="text-right">{Number(it.qty || 0).toLocaleString("id-ID")} {it.unit}</td><td className="text-right">{money(it.landed_unit, s.currency)}</td><td className="text-right">{money(it.sell_price, s.currency)}</td><td className="text-right">{money(it.line_total, s.currency)}</td></tr>)}
      </tbody>
    </table>
    <p className="p-2 text-xs text-slate-600">Customer: <b>{s.customer_name}</b> • Project: {s.project_title || "-"}</p>
  </div>
);

export const CostingRevisions = ({ costing, onChanged, beforeRevise }) => {
  const [revOpen, setRevOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [note, setNote] = useState("");
  const [fromRev, setFromRev] = useState(null);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  useEffect(() => { if (histOpen) api.get(`/costings/${costing.id}/revisions`).then((r) => setRows(r.data)); }, [histOpen, costing.id, costing.rev_no]);

  const revise = async () => {
    if (beforeRevise && !fromRev && !(await beforeRevise())) return;
    try {
      const { data } = await api.post(`/costings/${costing.id}/revise`, { note, from_rev_id: fromRev?.id || "" });
      toast.success(`Rev.${data.rev_no} dibuat — versi sebelumnya tersimpan di riwayat`);
      setRevOpen(false); setHistOpen(false); setNote(""); setFromRev(null);
      onChanged(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const startRev = (r = null) => { setFromRev(r); setNote(""); setRevOpen(true); };
  const cur = { rev_no: costing.rev_no || 0, note: costing.rev_note, created_by_name: costing.rev_by || costing.sales_name, created_at: costing.rev_at || costing.created_at, snapshot: costing, current: true };

  return (
    <>
      <Button variant="outline" className="bg-white" onClick={() => startRev()} data-testid="costing-revise-btn"><GitBranch className="h-4 w-4 mr-1" />Revisi</Button>
      <Button variant="outline" className="bg-white" onClick={() => setHistOpen(true)} data-testid="costing-rev-history-btn"><History className="h-4 w-4 mr-1" />Riwayat Revisi</Button>
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Buat Rev.{(costing.rev_no || 0) + 1}{fromRev ? ` dari Rev.${fromRev.rev_no}` : ""}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Versi saat ini (Rev.{costing.rev_no || 0}) akan disimpan utuh di Riwayat Revisi. {beforeRevise && !fromRev ? "Perubahan yang belum disimpan akan otomatis disimpan ke versi ini dulu." : ""}</p>
          <Textarea className="bg-white" placeholder="Catatan / alasan revisi (opsional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="costing-rev-note-input" />
          <Button onClick={revise} data-testid="costing-rev-confirm-btn">Buat Revisi</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={histOpen} onOpenChange={(o) => { setHistOpen(o); setView(null); }}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="h-5 w-5 text-blue-800" />Riwayat Revisi {costing.costing_no}</DialogTitle></DialogHeader>
          <ol className="relative ml-3 space-y-4 border-l-2 border-blue-100" data-testid="costing-rev-timeline">
            {[cur, ...rows].map((r, i) => (
              <li key={r.id || "cur"} className="ml-6" data-testid={`costing-rev-item-${i}`}>
                <span className={`absolute -left-[9px] mt-1.5 h-4 w-4 rounded-full border-2 border-white ${r.current ? "bg-blue-800" : "bg-rose-500"}`} />
                <div className="card-premium p-4 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-semibold">Rev.{r.rev_no} {r.current && <span className="ml-1 rounded bg-blue-800 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Aktif</span>}</span>
                    <span className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setView(view === i ? null : i)} data-testid={`costing-rev-view-${i}`}><Eye className="h-4 w-4 mr-1" />Detail</Button>
                      {!r.current && <Button size="sm" variant="ghost" className="text-blue-800" onClick={() => startRev(r)} data-testid={`costing-rev-use-${i}`}><RotateCcw className="h-4 w-4 mr-1" />Pakai versi ini</Button>}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{fdate(r.created_at)} • oleh {r.created_by_name}</p>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <div><p className="text-[11px] text-slate-400">Total Cost</p><p className="font-num font-semibold">{money(r.snapshot.total_cost, r.snapshot.currency)}</p></div>
                    <div><p className="text-[11px] text-slate-400">Harga Penawaran</p><p className="font-num font-semibold">{money(r.snapshot.offer_total, r.snapshot.currency)}</p></div>
                    <div><p className="text-[11px] text-slate-400">Margin</p><p className="font-num font-semibold">{pct(r.snapshot.final_margin)}</p></div>
                  </div>
                  {r.note && <p className="mt-2 rounded bg-rose-50 p-2 text-xs text-rose-800"><b>Catatan Revisi:</b> {r.note}</p>}
                  {view === i && <Detail s={r.snapshot} />}
                </div>
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
};
